/**
 * A profile command that can never run as a program (Terminal board card 6,
 * 21.10.2026). The owner's friend picked /Applications/WezTerm.app with
 * Browse… on a Mac and got "was not found on your PATH" — true, but no help.
 * We name the real problem and what to use instead:
 *
 *   'app'       a macOS .app bundle — use the program inside it
 *   'shortcut'  a Windows .lnk — use the program it points to
 *   'folder'    a directory — choose the program file in it (Rust tells us)
 *
 * `detected` is the Rust detection's `problem` for this command, if any.
 */
export function profileProblem(command, detected) {
  const c = String(command || '').trim();
  if (!c) return null;
  if (/\.app[\\/]?$/i.test(c)) return 'app';
  if (/\.lnk$/i.test(c)) return 'shortcut';
  return detected === 'app' || detected === 'folder' ? detected : null;
}

/** "WezTerm.app" from "/Applications/WezTerm.app/". */
export const baseName = (command) => String(command || '').replace(/[\\/]+$/, '').split(/[\\/]/).pop();

/**
 * Where a Mac app usually keeps its command-line program:
 * /Applications/WezTerm.app → /Applications/WezTerm.app/Contents/MacOS/wezterm.
 */
export function appCliGuess(command) {
  const dir = String(command || '').replace(/[\\/]+$/, '');
  const name = baseName(dir).replace(/\.app$/i, '').toLowerCase().replace(/\s+/g, '');
  return `${dir}/Contents/MacOS/${name}`;
}

/** The sentence for the problem, through `t`. */
export function problemMessage(t, problem, command) {
  const name = baseName(command);
  if (problem === 'app') {
    return t(
      'terminalExt.problemApp',
      '{{name}} is an app, not a command-line program. A profile runs a program inside this terminal — for the app’s own tool use the program inside it, usually {{cli}}.',
      { name, cli: appCliGuess(command) },
    );
  }
  if (problem === 'shortcut') {
    return t('terminalExt.problemShortcut', '{{name}} is a Windows shortcut. Use the program it points to (right-click → Properties → Target).', { name });
  }
  if (problem === 'folder') {
    return t('terminalExt.problemFolder', '{{name}} is a folder. Choose the program file inside it.', { name });
  }
  return null;
}
